import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu, Search, Bell, ChevronDown, LogOut, Bot, User,
  Sun, Moon, CheckCheck, Trash2, Info, CheckCircle, AlertTriangle, XCircle,
} from 'lucide-react';
import { getLogs, markAllRead, clearLogs } from '../utils/logger';

// ── Helpers de logs ─────────────────────────────────────────────
const LOG_EVENT = 'legalplus-log';

const TYPE_CONFIG = {
  success: { icon: CheckCircle,    color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  error:   { icon: XCircle,        color: 'text-red-400',     bg: 'bg-red-500/10'     },
  warning: { icon: AlertTriangle,  color: 'text-amber-400',   bg: 'bg-amber-500/10'   },
  info:    { icon: Info,           color: 'text-blue-400',    bg: 'bg-blue-500/10'    },
};

function formatTimestamp(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1)  return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)   return `Hace ${diffH}h`;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

// ── ThemeToggle ──────────────────────────────────────────────────
const ThemeToggle = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <button
      onClick={toggle}
      className="p-2 rounded-brand bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-white/[0.1] text-brand-silver-dim hover:text-brand-silver transition-all duration-[250ms] ease-brand active:scale-95"
      title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      aria-label="Toggle theme"
    >
      {theme === 'dark'
        ? <Sun  size={16} className="text-brand-amber" />
        : <Moon size={16} className="text-brand-indigo-soft" />
      }
    </button>
  );
};

// ── NotificationsPanel ───────────────────────────────────────────
const NotificationsPanel = () => {
  const [open, setOpen]   = useState(false);
  const [logs, setLogs]   = useState(() => getLogs());
  const panelRef          = useRef(null);

  // Refrescar cuando otro componente dispara el evento
  useEffect(() => {
    const refresh = () => setLogs(getLogs());
    window.addEventListener(LOG_EVENT, refresh);
    return () => window.removeEventListener(LOG_EVENT, refresh);
  }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const unread = logs.filter(l => !l.read).length;

  const handleOpen = () => {
    setOpen(v => !v);
    if (!open && unread > 0) {
      markAllRead();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    clearLogs();
    setLogs([]);
  };

  return (
    <div ref={panelRef} className="relative">
      {/* Botón campana */}
      <button
        onClick={handleOpen}
        className="p-2 rounded-brand relative transition-all duration-[250ms] ease-brand hover:bg-white/[0.05] border border-white/[0.06] hover:border-white/[0.1] active:scale-95 group"
        title="Logs de actividad"
      >
        <Bell size={18} className={`transition-colors ${open ? 'text-brand-indigo' : 'text-brand-silver-dim group-hover:text-brand-silver'}`} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center leading-none"
            style={{
              background: 'linear-gradient(135deg, #F15B5B 0%, #B83A3A 100%)',
              boxShadow: '0 0 0 2px #050B18, 0 0 10px rgba(241,91,91,0.5)',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Panel dropdown */}
      {open && (
        <div className="absolute top-12 right-0 w-80 glass-brand-strong rounded-brand-lg z-50 animate-scaleIn overflow-hidden">
          {/* Header del panel */}
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between bg-gradient-to-r from-white/[0.04] to-transparent">
            <div>
              <p className="text-sm font-semibold text-white">Actividad del sistema</p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {logs.length === 0 ? 'Sin registros' : `${logs.length} registro${logs.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            {logs.length > 0 && (
              <button
                onClick={handleClear}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-400 transition-colors px-2 py-1 rounded-lg hover:bg-red-500/10"
                title="Limpiar todos los logs"
              >
                <Trash2 size={13} />
                Limpiar
              </button>
            )}
          </div>

          {/* Lista de logs */}
          <div className="max-h-80 overflow-y-auto custom-scrollbar">
            {logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center mb-2">
                  <Bell size={18} className="text-slate-600" />
                </div>
                <p className="text-xs text-slate-500">No hay actividad registrada</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-800/60">
                {logs.map(log => {
                  const cfg = TYPE_CONFIG[log.type] || TYPE_CONFIG.info;
                  const Icon = cfg.icon;
                  return (
                    <li key={log.id} className={`flex gap-3 px-4 py-3 hover:bg-slate-800/40 transition-colors ${!log.read ? 'bg-slate-800/20' : ''}`}>
                      <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                        <Icon size={14} className={cfg.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {log.module && (
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                              {log.module}
                            </span>
                          )}
                          {log.user && (
                            <span className="text-[10px] text-blue-400/80 font-medium">
                              · {log.user}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-200 leading-snug break-words mt-0.5">{log.message}</p>
                        <p className="text-[10px] text-slate-600 mt-1">{formatTimestamp(log.timestamp)}</p>
                      </div>
                      {!log.read && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {logs.length > 0 && (
            <div className="px-4 py-2.5 border-t border-slate-800 bg-slate-900/80">
              <button
                onClick={() => { markAllRead(); setLogs(getLogs()); }}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-400 transition-colors"
              >
                <CheckCheck size={13} />
                Marcar todo como leído
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Header principal ─────────────────────────────────────────────
const Header = ({ toggleSidebar, searchTerm, handleSearch, onLogout, toggleChat, user }) => {
  const [isScrolled, setIsScrolled]       = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 5);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getRolLabel = (rol) => {
    const map = { Administrador: 'Admin', Abogado: 'Abogado', Contabilidad: 'Contabilidad' };
    return map[rol] || rol || '';
  };

  return (
    <header
      className={`
        px-5 py-3 flex items-center justify-between sticky top-0 z-30
        transition-all duration-[300ms] ease-brand
        ${isScrolled
          ? 'bg-brand-night/92 backdrop-blur-xl border-b border-brand-indigo/10 shadow-brand-soft'
          : 'bg-brand-night/75 backdrop-blur-md border-b border-white/[0.04]'
        }
      `}
    >
      {/* Hairline superior luminosa */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-indigo/30 to-transparent" />

      {/* ── Izquierda: toggle sidebar + buscador ── */}
      <div className="flex items-center flex-1 gap-3">
        <button
          onClick={toggleSidebar}
          className="text-brand-silver-dim lg:hidden p-2 rounded-brand hover:bg-white/[0.05] hover:text-brand-silver transition-all duration-200 active:scale-95"
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>

        <div className="relative w-full max-w-sm hidden sm:block group">
          <input
            type="text"
            placeholder="Buscar casos, clientes, abogados…"
            value={searchTerm}
            onChange={handleSearch}
            className="w-full bg-white/[0.03] text-brand-silver placeholder:text-brand-silver-mute/70 pl-10 pr-14 py-2.5 rounded-brand border border-white/[0.06] focus:border-brand-indigo/50 focus:ring-2 focus:ring-brand-indigo/20 focus:bg-white/[0.05] focus:outline-none text-[13px] transition-all duration-[280ms] ease-brand"
          />
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-silver-mute group-focus-within:text-brand-indigo transition-colors" />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-0.5 px-1.5 py-0.5 rounded font-mono text-[9px] font-semibold text-brand-silver-mute bg-white/[0.04] border border-white/[0.06]">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* ── Derecha: acciones ── */}
      <div className="flex items-center gap-1.5 ml-2">
        <ThemeToggle />

        {/* Botón IA — acento "sistema inteligente" con cyan */}
        <button
          onClick={toggleChat}
          className="p-2 rounded-brand relative transition-all duration-[250ms] ease-brand hover:bg-brand-cyan/10 border border-white/[0.06] hover:border-brand-cyan/40 active:scale-95 group"
          title="Abrir IA Asistente"
        >
          <Bot size={18} className="text-brand-silver-dim group-hover:text-brand-cyan transition-colors" />
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-brand-cyan"
            style={{ boxShadow: '0 0 0 2px #050B18, 0 0 8px rgba(92,225,230,0.7)' }}
          />
        </button>

        {/* Notificaciones / Logs */}
        <NotificationsPanel />

        {/* Perfil */}
        <div className="relative ml-1">
          <button
            onClick={() => setShowProfileMenu(v => !v)}
            className="flex items-center gap-2.5 cursor-pointer pl-1 pr-2.5 py-1 rounded-brand transition-all duration-[250ms] ease-brand hover:bg-white/[0.05] border border-white/[0.06] hover:border-white/[0.1] active:scale-[0.98]"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #4F7DFF 0%, #2B4EB8 100%)',
                boxShadow: '0 0 0 1px rgba(79,125,255,0.3)',
              }}
            >
              {getInitials(user?.nombre)}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-[12px] font-semibold text-brand-silver leading-none truncate max-w-[110px]">{user?.nombre || 'Usuario'}</p>
              <p className="text-[9px] tracking-[0.2em] font-semibold text-brand-silver-mute leading-none mt-1 uppercase">{getRolLabel(user?.rol)}</p>
            </div>
            <ChevronDown size={13} className={`text-brand-silver-mute hidden sm:block transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
          </button>

          {showProfileMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowProfileMenu(false)} />
              <div className="absolute top-12 right-0 w-56 glass-brand-strong rounded-brand-lg p-1.5 z-50 animate-scaleIn">
                <div className="px-3 py-3 mb-1 border-b border-white/[0.06]">
                  <p className="text-brand-silver font-semibold text-sm truncate">{user?.nombre}</p>
                  <p className="eyebrow text-brand-silver-mute mt-1.5">{getRolLabel(user?.rol)}</p>
                </div>
                <ul className="space-y-0.5 pt-1">
                  <li>
                    <Link
                      to="/perfil"
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-brand text-[13px] text-brand-silver-dim hover:bg-white/[0.05] hover:text-brand-silver transition-colors duration-150"
                    >
                      <User size={15} className="text-brand-silver-mute" />
                      <span>Mi perfil</span>
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-brand text-[13px] text-brand-silver-dim hover:bg-danger-soft hover:text-danger-DEFAULT transition-colors duration-150"
                    >
                      <LogOut size={15} className="text-brand-silver-mute" />
                      <span>Cerrar sesión</span>
                    </button>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
